import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, X, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WhatsAppAlert() {
  const [showAlert, setShowAlert] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Verificar se já mostrou o alerta nesta sessão
    const alertShown = sessionStorage.getItem('whatsapp_alert_shown');
    
    if (!alertShown) {
      checkWhatsAppStatus();
    } else {
      setLoading(false);
    }
  }, []);

  const checkWhatsAppStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/whatsapp/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const status = response.data;
      setWhatsappStatus(status);
      
      // Mostrar alerta se não estiver conectado
      if (!status.connected) {
        setShowAlert(true);
      }
      
      // Marcar que o alerta foi mostrado nesta sessão
      sessionStorage.setItem('whatsapp_alert_shown', 'true');
      
    } catch (error) {
      console.error('Error checking WhatsApp status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowAlert(false);
  };

  const handleGoToWhatsApp = () => {
    setShowAlert(false);
    navigate('/whatsapp');
  };

  if (loading || !showAlert) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full p-6 shadow-2xl animate-in slide-in-from-bottom-4 duration-300">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <AlertCircle className="w-6 h-6 text-orange-500" />
            </div>
            <h3 className="text-xl font-bold text-white">WhatsApp Desconectado</h3>
          </div>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4">
          <p className="text-slate-300">
            Seu WhatsApp está desconectado ou precisa ser conectado novamente.
          </p>

          {whatsappStatus?.status === 'qr_code' ? (
            <div className="p-3 bg-emerald-900/20 border border-emerald-700 rounded-lg">
              <p className="text-sm text-emerald-300">
                ✅ QR Code já está disponível! Basta escanear para conectar.
              </p>
            </div>
          ) : whatsappStatus?.status === 'service_unavailable' ? (
            <div className="p-3 bg-rose-900/20 border border-rose-700 rounded-lg">
              <p className="text-sm text-rose-300">
                ⚠️ Serviço WhatsApp não disponível. Verifique a configuração.
              </p>
            </div>
          ) : (
            <div className="p-3 bg-violet-900/20 border border-violet-700 rounded-lg">
              <p className="text-sm text-violet-300">
                💡 Clique no botão abaixo para ir à página do WhatsApp e conectar.
              </p>
            </div>
          )}

          <ul className="text-sm text-slate-400 space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-violet-500 mt-0.5">•</span>
              <span>Muitas funcionalidades dependem do WhatsApp conectado</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-violet-500 mt-0.5">•</span>
              <span>Envio automático de mensagens não funcionará</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-violet-500 mt-0.5">•</span>
              <span>Renovações não enviarão notificações por WhatsApp</span>
            </li>
          </ul>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2 border border-zinc-700 text-slate-300 rounded-lg hover:bg-zinc-800 transition-colors"
          >
            Depois
          </button>
          <button
            onClick={handleGoToWhatsApp}
            className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
          >
            <MessageSquare className="w-4 h-4" />
            Conectar Agora
          </button>
        </div>
      </div>
    </div>
  );
}
